import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { buildEmailCallbackUrl, normalizeAuthReturnTo } from '@/routing/authReturnTo';
import { getOAuthCallbackOrigin } from '@/routing/oauthCallbackOrigin';
import { vendorOnboardingPath } from '@/routing/firstLogin';

interface AuthUser {
  id?: string;
  email: string;
  name: string;
  picture?: string;
  access_token?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
    bio?: string;
    location?: string;
    // Admin-managed first-login flag (admin-onboard-vendor edge function);
    // cleared server-side by /api/onboarding/complete. Read by the post-login
    // routing in Auth.tsx and the session-restore effect below.
    vendor_onboarding?: boolean;
  };
}

interface LoginCredentials {
  email: string;
  password: string;
  name?: string;
}

interface SignUpMetadata {
  full_name?: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<AuthUser | null>;
  signUp: (email: string, password: string, metadata?: SignUpMetadata, returnTo?: string | null) => Promise<{ data: any; error: any }>;
  resendVerificationEmail: (email: string, returnTo?: string | null) => Promise<void>;
  signInWithGoogle: (redirectUrl?: string) => Promise<void>;
  signInWithFacebook: (redirectUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<boolean>;
  validateSession: () => Promise<boolean>;
}

const DEFAULT_AVATAR_URL = 'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png';

// Fire-and-forget welcome email after signup verification / first OAuth
// sign-in. The endpoint resolves the recipient from the access token and
// dedupes via a server-side flag, so repeat calls never double-send.
const requestWelcomeEmail = (accessToken?: string) => {
  if (!accessToken) return;
  fetch('/api/email/welcome', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch((error) => {
    console.warn('Welcome email request failed', error);
  });
};

// Helper function to check if user data has actually changed
const isUserDataEqual = (user1: AuthUser | null, user2: AuthUser | null): boolean => {
  if (user1 === user2) return true;
  if (!user1 || !user2) return user1 === user2;

  // Compare all relevant fields
  // NOTE: We deliberately exclude access_token from comparison because it changes
  // on every token refresh (~hourly) but doesn't represent a change in user identity.
  // Comparing access_token would cause "User data changed" on every refresh, triggering
  // unnecessary re-renders and WebSocket reconnections across the entire app.
  return (
    user1.id === user2.id &&
    user1.email === user2.email &&
    user1.name === user2.name &&
    user1.picture === user2.picture &&
    // user1.access_token === user2.access_token &&  // Excluded - see note above
    user1.user_metadata?.full_name === user2.user_metadata?.full_name &&
    user1.user_metadata?.avatar_url === user2.user_metadata?.avatar_url &&
    user1.user_metadata?.bio === user2.user_metadata?.bio &&
    user1.user_metadata?.location === user2.user_metadata?.location &&
    user1.user_metadata?.vendor_onboarding === user2.user_metadata?.vendor_onboarding
  );
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to track last token refresh timestamp for debouncing
  const lastTokenRefresh = useRef<number>(0);

  useEffect(() => {
    // Check for existing Supabase session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Authentication session lookup failed');
        
        // Handle specific error types
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('Authentication service rejected the session request');
          // Show user-friendly error for persistent 406 errors
          toast.error('Authentication service temporarily unavailable. Please try refreshing the page.');
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.warn('Unauthorized session, setting as unauthenticated');
        } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
          console.error('Authentication network request failed');
          toast.error('Network connection issue. Please check your internet connection.');
        } else {
          console.error('Authentication session request failed');
        }
        
        // Set as unauthenticated regardless of error type
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      if (session) {
        const userData: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
          picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: session.access_token,
          user_metadata: {
            full_name: session.user.user_metadata.full_name,
            avatar_url: session.user.user_metadata.avatar_url,
            bio: session.user.user_metadata.bio,
            location: session.user.user_metadata.location,
            vendor_onboarding: session.user.user_metadata.vendor_onboarding === true,
          }
        };
        // Initial session: just set directly (no previous user to compare)
        setUser(userData);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    }).catch((error) => {
      console.error('Authentication session initialization failed');
      
      // Handle different error types
      if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
        console.error('Authentication service rejected the session initialization');
      }
      
      // Set as unauthenticated on any error
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    });

    // Check for email confirmation parameters in URL
    const handleEmailConfirmation = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const urlParams = new URLSearchParams(window.location.search);
      
      // Check for email confirmation tokens in hash or query params
      const accessToken = hashParams.get('access_token') || urlParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || urlParams.get('refresh_token');
      const type = hashParams.get('type') || urlParams.get('type');
      
      if (accessToken && refreshToken && type === 'email_confirmation') {
        try {
          console.log('Processing email confirmation...');
          
          // Set the session from the confirmation tokens
          const { data: { session }, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Email confirmation session setup failed');
            return;
          }

          if (session) {
            console.log('✅ Email confirmation successful, user authenticated');
            
            // Clear the tokens from URL to prevent reprocessing
            const newURL = window.location.pathname;
            window.history.replaceState(null, '', newURL);
            
            // Store confirmation state for welcome flow
            sessionStorage.setItem('email-just-confirmed', 'true');
            sessionStorage.setItem('confirmed-email', session.user.email || '');

            // Send the welcome email now that signup verification completed.
            requestWelcomeEmail(session.access_token);
            
            // Show immediate confirmation success
            toast.success('Email Verified Successfully! 🎉', {
              description: 'Your account is now active. Preparing your welcome...',
              duration: 4000
            });
            
            // The onAuthStateChange listener will handle the UI updates
            // Dispatch enhanced welcome event after UI is ready
            setTimeout(() => {
              if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('email-confirmed', {
                  detail: { 
                    email: session.user.email,
                    isNewUser: true,
                    timestamp: new Date().toISOString()
                  }
                }));
              }
            }, 1000);
          }
        } catch {
          console.error('Email confirmation processing failed');
        }
      }
    };

    // Check for email confirmation after initial session check
    handleEmailConfirmation();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        console.log('Auth state changed:', event);

        // Debounce rapid TOKEN_REFRESHED events to prevent cascading re-renders
        if (event === 'TOKEN_REFRESHED') {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastTokenRefresh.current;
          if (timeSinceLastRefresh < 5000) {
            console.log(`Skipping rapid token refresh event (${timeSinceLastRefresh}ms since last)`);
            return;
          }
          lastTokenRefresh.current = now;
        }

        if (session) {
          const userData: AuthUser = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
            picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
            access_token: session.access_token,
            user_metadata: {
              full_name: session.user.user_metadata.full_name,
              avatar_url: session.user.user_metadata.avatar_url,
              bio: session.user.user_metadata.bio,
              location: session.user.user_metadata.location,
              vendor_onboarding: session.user.user_metadata.vendor_onboarding === true,
            }
          };

          // Only update state if user data has actually changed
          // This prevents unnecessary re-renders on token refresh events
          setUser(prevUser => {
            if (isUserDataEqual(prevUser, userData)) {
              console.log('Auth user data unchanged, skipping state update');
              return prevUser; // Return previous reference to prevent re-render
            }
            console.log('Auth user data changed, updating state');
            return userData;
          });
          setIsAuthenticated(true);
          setIsLoading(false);
          
          // For OAuth users, check if profile exists and create if needed (fallback)
          if (event === 'SIGNED_IN' && session.user.app_metadata?.provider !== 'email') {
            // OAuth signups verify implicitly — welcome email fires here too,
            // deduped server-side by the welcome_email_sent_at flag.
            requestWelcomeEmail(session.access_token);

            try {
              await createUserProfileFallback(session.user.id, session.user.email || '', {
                full_name: session.user.user_metadata.full_name,
                avatar_url: session.user.user_metadata.avatar_url
              });
            } catch (profileError) {
              console.warn('OAuth profile fallback creation failed');
              // Don't fail the sign-in process
            }
          }
          
          // If this is from email confirmation, show success message
          if (event === 'SIGNED_IN' && window.location.hash.includes('type=email_confirmation')) {
            setTimeout(() => {
              console.log('Email confirmed and signed in successfully');
            }, 500);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Auth state change handling failed');
        
        // Handle different error types
        if (error instanceof Error) {
          if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
            console.error('Authentication service rejected the auth state update');
          }
        }
        
        // Set as unauthenticated on any error
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Add effect to handle auth-required events
  useEffect(() => {
    const handleAuthRequired = (event: CustomEvent) => {
      if (!isAuthenticated) {
        // Store the action and data to be executed after auth
        localStorage.setItem('auth_pending_action', JSON.stringify(event.detail));
      }
    };

    window.addEventListener('auth-required', handleAuthRequired as EventListener);
    return () => window.removeEventListener('auth-required', handleAuthRequired as EventListener);
  }, [isAuthenticated]);

  // Add effect to handle pending actions after authentication
  useEffect(() => {
    if (isAuthenticated) {
      const pendingAction = localStorage.getItem('auth_pending_action');
      if (pendingAction) {
        const { action, ...data } = JSON.parse(pendingAction);
        // Execute the pending action
        if (action === 'favorite') {
          // Dispatch an event to trigger the favorite action
          window.dispatchEvent(new CustomEvent('execute-favorite', { detail: data }));
        }
        localStorage.removeItem('auth_pending_action');
      }

      // Check for auth redirect URL (e.g., from SellerDashboard)
      const storedAuthRedirect = sessionStorage.getItem('auth_redirect');
      if (storedAuthRedirect) {
        // First-login vendors finish onboarding before their stored
        // destination; the stored redirect is consumed either way so a
        // full-page reload cannot loop on the same decision.
        const target = vendorOnboardingPath(user)
          ?? normalizeAuthReturnTo(storedAuthRedirect);
        sessionStorage.removeItem('auth_redirect');
        // Use setTimeout to ensure navigation happens after auth state is fully settled
        setTimeout(() => {
          window.location.href = target;
        }, 100);
      }
    }
  }, [isAuthenticated]);

  // Listen for email confirmation success events
  useEffect(() => {
    const handleEmailConfirmed = (event: CustomEvent) => {
      const { email } = event.detail;
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: {
              type: 'success',
              title: 'Email Confirmed!',
              description: `Welcome! Your email ${email} has been verified and you're now signed in.`
            }
          }));
        }
      }, 1000);
    };

    window.addEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    return () => window.removeEventListener('email-confirmed', handleEmailConfirmed as EventListener);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('Starting login process');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        // Create enhanced error object for email confirmation errors
        if (error.message?.toLowerCase().includes('email not confirmed') || 
            error.message?.toLowerCase().includes('email_not_confirmed')) {
          const enhancedError = new Error(error.message);
          (enhancedError as any).isEmailNotConfirmed = true;
          (enhancedError as any).originalError = error;
          throw enhancedError;
        }
        throw error;
      }

      if (data.user) {
        console.log('Auth login successful');
        
        const userData: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata.full_name || data.user.email?.split('@')[0] || '',
          picture: data.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: data.session?.access_token,
          user_metadata: {
            full_name: data.user.user_metadata.full_name,
            avatar_url: data.user.user_metadata.avatar_url,
            bio: data.user.user_metadata.bio,
            location: data.user.user_metadata.location,
            vendor_onboarding: data.user.user_metadata.vendor_onboarding === true,
          }
        };
        setUser(userData);
        setIsAuthenticated(true);
        // Returned so callers (Auth.tsx post-login routing) can honor
        // first-login flags like vendor_onboarding without another round trip.
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Login failed');
      throw error;
    }
  };

  // Fallback profile creation for OAuth users or edge cases
  const createUserProfileFallback = async (userId: string, email: string, metadata?: SignUpMetadata) => {
    try {
      console.log('Creating fallback user profile');
      
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (existingProfile) {
        console.log('✅ Profile already exists, skipping creation');
        return { data: existingProfile, error: null };
      }
      
      // Generate a unique username from email
      const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
      
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          username,
          first_name: metadata?.full_name || email.split('@')[0],
          full_name: metadata?.full_name || email.split('@')[0],
          display_name: metadata?.full_name || email.split('@')[0], // Sync display_name with full_name
          avatar_url: metadata?.avatar_url || DEFAULT_AVATAR_URL,
        })
        .select('id')
        .single();

      if (error) {
        console.error('Fallback user profile creation failed');
        throw error;
      }

      console.log('Fallback user profile created successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Fallback user profile creation failed');
      return { data: null, error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: SignUpMetadata, returnTo: string | null = null) => {
    try {
      console.log('Starting signup process');
      
      // Try Supabase standard signup first
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: buildEmailCallbackUrl(window.location.origin, returnTo),
          data: {
            full_name: metadata?.full_name || email.split('@')[0],
            avatar_url: metadata?.avatar_url || DEFAULT_AVATAR_URL,
          }
        }
      });

      if (error) {
        console.error('Signup request failed');
        throw error;
      }

      // Check if email confirmation is required
      if (data.user && !data.user.email_confirmed_at) {
        console.log('📧 Email confirmation required - user needs to verify email');
        return { 
          data: { 
            ...data, 
            requiresEmailConfirmation: true,
            message: 'Please check your email and click the confirmation link to complete your signup.'
          }, 
          error: null 
        };
      }

      console.log('Signup successful - user is confirmed');
      return { data, error: null };

    } catch (error) {
      console.error('Signup process failed');
      throw error;
    }
  };

  // Rejects on failure: every caller shows "sent" after the await and its
  // error message in a catch, so a swallowed error reads as success.
  const resendVerificationEmail = async (email: string, returnTo: string | null = null): Promise<void> => {
    // Without emailRedirectTo Supabase falls back to the Site URL, which
    // sends studio signups to the marketplace origin.
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: { emailRedirectTo: buildEmailCallbackUrl(window.location.origin, returnTo) },
    });

    if (error) {
      console.error('Verification email resend failed');
      throw error;
    }
  };

  const signInWithGoogle = async (redirectUrl?: string) => {
    try {
      // Determine a safe redirect target. If none provided, fall back to current location.
      const currentPath = typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '/';
      const redirectTarget = normalizeAuthReturnTo(redirectUrl, currentPath);

      // Persist intended redirect so we can restore even if query params are lost during OAuth
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_redirect', redirectTarget);
      }

      const callbackUrl = `${getOAuthCallbackOrigin()}/auth/callback?returnTo=${encodeURIComponent(redirectTarget)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign-in request failed');
      throw error;
    }
  };

  const signInWithFacebook = async (redirectUrl?: string) => {
    try {
      // Determine a safe redirect target. If none provided, fall back to current location.
      const currentPath = typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '/';
      const redirectTarget = normalizeAuthReturnTo(redirectUrl, currentPath);

      // Persist intended redirect so we can restore even if query params are lost during OAuth
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_redirect', redirectTarget);
      }

      const callbackUrl = `${getOAuthCallbackOrigin()}/auth/callback?returnTo=${encodeURIComponent(redirectTarget)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: callbackUrl,
          scopes: 'email',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Facebook sign-in request failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed');
      throw error;
    }
  };

  const getAccessToken = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Access-token lookup failed');
        
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('Authentication service rejected the access-token request');
        }
        
        return null;
      }
      
      return session?.access_token || null;
    } catch {
      console.error('Access-token lookup failed');
      return null;
    }
  };

  const refreshSession = async () => {
    try {
      // Try to refresh the session with retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempts < maxAttempts) {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            lastError = error;
            console.error(`Session refresh attempt ${attempts + 1} failed`);
            
            if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
              console.error('Authentication service rejected the session refresh');
            }
            
            // If it's a network error, wait and retry
            if (error.message?.includes('network') || error.message?.includes('fetch') || error.message?.includes('ERR_NETWORK_CHANGED')) {
              attempts++;
              if (attempts < maxAttempts) {
                console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
            }
            
            // For other errors, don't retry
            break;
          }
          
          if (session) {
            const userData: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
              picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
              access_token: session.access_token,
              user_metadata: {
                full_name: session.user.user_metadata.full_name,
                avatar_url: session.user.user_metadata.avatar_url,
                bio: session.user.user_metadata.bio,
                location: session.user.user_metadata.location,
              }
            };

            // Only update state if user data has actually changed
            setUser(prevUser => {
              if (isUserDataEqual(prevUser, userData)) {
                return prevUser; // Prevent re-render on session refresh
              }
              return userData;
            });
            setIsAuthenticated(true);
            return true;
          } else {
            setUser(null);
            setIsAuthenticated(false);
            return false;
          }
        } catch (networkError) {
          lastError = networkError;
          console.error(`Session refresh attempt ${attempts + 1} failed with a network error`);
          attempts++;
          
          if (attempts < maxAttempts) {
            console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      // All attempts failed
      console.error('All session refresh attempts failed');
      
      // Show user-friendly error for critical session failures
      if (lastError && typeof lastError === 'object' && 'message' in lastError) {
        const errorMessage = String(lastError.message);
        if (errorMessage.includes('406') || errorMessage.includes('Not Acceptable')) {
          toast.error('Session expired. Please sign in again.');
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          toast.error('Connection lost. Please check your internet and try again.');
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
      
    } catch (error) {
      console.error('Unexpected session refresh failure');
      
      if (error instanceof Error) {
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('Authentication service rejected the session refresh');
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  const validateSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('Session validation failed, attempting refresh...');
        return await refreshSession();
      }
      
      // Check if session is expired or about to expire (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const expiresInSeconds = expiresAt - now;
      
      if (expiresInSeconds < 300) { // Less than 5 minutes
        console.log('Session expires soon, refreshing...');
        return await refreshSession();
      }
      
      return true;
    } catch {
      console.error('Session validation failed');
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, signUp, resendVerificationEmail, signInWithGoogle, signInWithFacebook, logout, getAccessToken, refreshSession, validateSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
